import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FileUp, History } from 'lucide-react';
import type { ClassificationImportList, ClassificationImportResult, ClassificationKind, ClassificationStatus, GoodsServicesRow, GoodsServicesSummary } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, Field, PageHeader, SearchableSelect, cn } from '../../components/ui';
import { CodeBrowser } from './CodeBrowser';
import { parseClassificationSearch, type ClassificationSearch } from './search';

const KINDS: ClassificationKind[] = ['isic', 'csi', 'lc'];

/** Goods & services: overview per list (zero counts kept), the browsers, and the Excel import with its history (admin action). */
export function ClassificationPage() {
  const { t, i18n } = useTranslation();
  const { canAction } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = parseClassificationSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const tab = search.tab ?? 'isic';
  const status = useQuery({ queryKey: ['classification', 'status'], queryFn: () => api.get<ClassificationStatus>('/api/classification/status') });
  const summary = useQuery({ queryKey: ['classification', 'summary'], queryFn: () => api.get<GoodsServicesSummary>('/api/classification/summary') });
  const imports = useQuery({ queryKey: ['classification', 'imports'], queryFn: () => api.get<ClassificationImportList>('/api/classification/imports'), enabled: tab === 'imports' });
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<ClassificationKind>('isic');
  const [result, setResult] = useState<ClassificationImportResult | null>(null);
  const upload = useMutation({
    mutationFn: () => api.upload<ClassificationImportResult>(`/api/classification/import?kind=${kind}`, file!),
    onSuccess: (r) => { setResult(r); setFile(null); toast.success(t('beneficiaries.classification.imported', { inserted: r.inserted, updated: r.updated })); void qc.invalidateQueries({ queryKey: ['classification'] }); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const setSearch = (patch: Partial<ClassificationSearch>) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
    void navigate({ to: '/beneficiaries/classification', search: next as ClassificationSearch });
  };
  const canImport = canAction('classification.import');
  const rows = tab === 'isic' ? summary.data?.isic : tab === 'csi' ? summary.data?.csi : tab === 'lc' ? summary.data?.lc : undefined;

  return (
    <div>
      <PageHeader title={t('beneficiaries.goodsServices')} subtitle={t('beneficiaries.classification.pageSubtitle')} />
      {status.data && (
        <div className="mb-3 grid gap-2 sm:grid-cols-3">
          {status.data.kinds.map((k) => (
            <button key={k.kind} type="button" onClick={() => setSearch({ tab: k.kind })} className={cn('card p-3 text-start', tab === k.kind && 'border-brand-600')}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">{t(`beneficiaries.classification.kinds.${k.kind}`)}</div>
              <div className="text-xl font-semibold tabular-nums">{k.active}</div>
              <div className="text-xs text-neutral-500">{t('beneficiaries.classification.rowsLine', { rows: k.rows, version: status.data!.pack_version })}{k.last_import_at && ` · ${t('beneficiaries.classification.lastImport', { date: new Date(k.last_import_at).toLocaleDateString(i18n.language) })}`}</div>
            </button>
          ))}
        </div>
      )}
      {summary.data && (
        <div className="mb-3 flex flex-wrap gap-4 text-xs text-neutral-500">
          <span>{t('beneficiaries.classification.totals.organisations', { count: summary.data.totals.organisations_with_activities })}</span>
          <span>{t('beneficiaries.classification.totals.activities', { count: summary.data.totals.activities })}</span>
          <span>{t('beneficiaries.classification.totals.csi', { count: summary.data.totals.csi_capacities })}</span>
          <span>{t('beneficiaries.classification.totals.lc', { count: summary.data.totals.lc_items })}</span>
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <div className="flex overflow-hidden rounded-lg border border-neutral-300 dark:border-neutral-700">
          {[...KINDS, 'imports' as const].map((k) => <button key={k} type="button" onClick={() => setSearch({ tab: k })} className={cn('px-3 py-1.5', tab === k ? 'bg-brand-600 text-white' : 'bg-white dark:bg-neutral-900')}>{k === 'imports' ? <span className="inline-flex items-center gap-1"><History size={14} /> {t('beneficiaries.classification.imports')}</span> : t(`beneficiaries.classification.kinds.${k}`)}</button>)}
        </div>
      </div>

      {tab !== 'imports' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="card p-4">
            <h2 className="mb-2 font-semibold">{t('beneficiaries.classification.browse')}</h2>
            <CodeBrowser kind={tab} />
          </div>
          <div className="card p-4">
            <h2 className="mb-2 font-semibold">{t('beneficiaries.classification.overview')}</h2>
            <p className="mb-2 text-xs text-neutral-500">{t('beneficiaries.classification.overviewHint')}</p>
            <OverviewTable rows={rows ?? []} />
          </div>
        </div>
      )}

      {tab === 'imports' && (
        <div className="grid gap-4 lg:grid-cols-[22rem_minmax(0,1fr)]">
          <div className="card p-4">
            <h2 className="mb-2 flex items-center gap-2 font-semibold"><FileUp size={16} className="text-neutral-500" />{t('beneficiaries.classification.importTitle')}</h2>
            {!canImport && <p className="text-sm text-neutral-500">{t('beneficiaries.classification.importNoAction')}</p>}
            {canImport && (
              <div className="grid gap-3">
                <Field label={t('beneficiaries.classification.importKind')}><SearchableSelect value={kind} onChange={(v) => setKind(v as ClassificationKind)} options={KINDS.map((k) => ({ value: k, label: t(`beneficiaries.classification.kinds.${k}`) }))} /></Field>
                <Field label={t('beneficiaries.classification.importFile')} hint={t(`beneficiaries.classification.columns.${kind}`)}><input type="file" className="text-xs" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
                <button type="button" className="btn-primary" disabled={!file || upload.isPending} onClick={() => upload.mutate()}>{upload.isPending ? t('app.loading') : t('beneficiaries.classification.importRun')}</button>
                <p className="text-xs text-neutral-500">{t('beneficiaries.classification.importHint')}</p>
              </div>
            )}
            {result && (
              <div className="mt-3 rounded-lg border border-neutral-200 p-3 text-sm dark:border-neutral-800">
                <div className="font-medium">{result.file_name}</div>
                <div className="text-xs text-neutral-500">{t('beneficiaries.classification.resultLine', { rows: result.rows, inserted: result.inserted, updated: result.updated, skipped: result.skipped })}</div>
                {result.errors.length > 0 && <ul className="mt-1 max-h-40 overflow-y-auto text-xs text-red-600">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
              </div>
            )}
          </div>
          <div className="card p-4">
            <h2 className="mb-2 font-semibold">{t('beneficiaries.classification.imports')}</h2>
            {imports.data && imports.data.items.length === 0 && <p className="text-sm text-neutral-500">{t('beneficiaries.classification.noImports')}</p>}
            <ul className="divide-y divide-neutral-100 text-sm dark:divide-neutral-800">
              {(imports.data?.items ?? []).map((i) => (
                <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div><Badge>{t(`beneficiaries.classification.kinds.${i.kind}`)}</Badge> <span className="font-medium">{i.file_name}</span><div className="text-xs text-neutral-500">{t('beneficiaries.classification.resultLine', { rows: i.rows, inserted: i.inserted, updated: i.updated, skipped: i.skipped })}{i.errors.length > 0 && ` · ${t('beneficiaries.classification.errorCount', { count: i.errors.length })}`}</div></div>
                  <div className="text-xs text-neutral-500">{new Date(i.imported_at).toLocaleString(i18n.language)}{i.imported_by && ` · ${pick(i18n.language, i.imported_by.display_name_en, i.imported_by.display_name_ar)}`}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewTable({ rows }: { rows: GoodsServicesRow[] }) {
  const { t, i18n } = useTranslation();
  const max = Math.max(1, ...rows.map((r) => r.beneficiaries));
  return (
    <div className="max-h-[32rem] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white text-start text-xs text-neutral-500 dark:bg-neutral-900"><tr><th className="py-1 text-start">{t('beneficiaries.classification.code')}</th><th className="py-1 text-start">{t('app.name')}</th><th className="py-1 text-end">{t('beneficiaries.kindsPlural.organisation')}</th><th className="py-1 text-end">{t('beneficiaries.classification.links')}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.code} className="border-t border-neutral-100 dark:border-neutral-800">
              <td className="py-1 pe-2"><code className="text-xs">{r.code}</code></td>
              <td className="py-1 pe-2" dir="auto">
                <div className="truncate">{pick(i18n.language, r.name_en, r.name_ar)}</div>
                <div className="h-1 rounded-full bg-neutral-100 dark:bg-neutral-800"><div className="h-full rounded-full bg-blue-500/70" style={{ width: `${(r.beneficiaries / max) * 100}%` }} /></div>
              </td>
              <td className={cn('py-1 text-end tabular-nums', r.beneficiaries === 0 && 'text-neutral-400')}>{r.beneficiaries}</td>
              <td className={cn('py-1 text-end tabular-nums', r.links === 0 && 'text-neutral-400')}>{r.links}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
